/* eslint-disable react-hooks/immutability */
'use client';

import { type RefObject, useCallback, useEffect } from 'react';
import useHaptic from 'src/hooks/use-haptic';

import type { GameConfig } from '@/app/(football)/[team]/mini-game/_components/Section/GameSection/BlockStackingGame/types';
import { DEFAULT_GAME_CONFIG } from '@/app/(football)/[team]/mini-game/_components/Section/GameSection/BlockStackingGame/types';

import { useAnimationLoop } from './useAnimationLoop';
import { useGameLogic } from './useGameLogic';
import { useGameState } from './useGameState';
import { usePhysicsWorld } from './usePhysicsWorld';
import { useRenderer } from './useRenderer';
import { useScene } from './useScene';

/**
 * Main block stacking game hook
 *
 * This hook composes several specialized hooks to build the game:
 * - Game state management (useGameState)
 * - Physics engine management (usePhysicsWorld)
 * - Rendering management (useRenderer, useScene)
 * - Game logic management (useGameLogic)
 * - Animation loop management (useAnimationLoop)
 */
export function useBlockStackingGame(
  containerRef: RefObject<HTMLDivElement | null>,
  gameConfig: GameConfig = DEFAULT_GAME_CONFIG,
) {
  const { triggerHaptic } = useHaptic();

  const {
    score,
    gameState,
    autopilotRef,
    gameEndedRef,
    robotPrecisionRef,
    setScore,
    setGameState,
    setRobotPrecision,
    resetGameState,
    startGame: startGameState,
    endGame,
  } = useGameState();

  const physics = usePhysicsWorld();
  const renderer = useRenderer(containerRef);
  const scene = useScene(containerRef, gameConfig);

  const gameLogic = useGameLogic({
    createPhysicsBody: physics.createPhysicsBody,
    updatePhysicsBodyShape: physics.updatePhysicsBodyShape,
    addMeshToScene: scene.addMeshToScene,
    removeMeshFromScene: scene.removeMeshFromScene,
    removePhysicsBody: physics.removePhysicsBody,
    gameConfig,
  });

  const calculateSpeed = useCallback(
    (stackLength: number) => {
      const { base, increment, max } = gameConfig.speed;
      const dynamicSpeed = base + stackLength * increment;
      return Math.min(dynamicSpeed, max);
    },
    [gameConfig.speed],
  );

  const onAnimationFrame = useCallback(
    (_time: number, timePassed: number) => {
      const speed = calculateSpeed(gameLogic.stackRef.current.length);

      const topLayer = gameLogic.stackRef.current[gameLogic.stackRef.current.length - 1];
      const previousLayer = gameLogic.stackRef.current[gameLogic.stackRef.current.length - 2];

      if (!topLayer?.direction || !previousLayer) return;

      const boxShouldMove =
        !gameEndedRef.current &&
        (!autopilotRef.current ||
          (autopilotRef.current &&
            topLayer.threejs.position[topLayer.direction] <
              previousLayer.threejs.position[topLayer.direction] + robotPrecisionRef.current));

      if (boxShouldMove) {
        topLayer.threejs.position[topLayer.direction] += speed * timePassed;
        topLayer.cannonjs.position[topLayer.direction] += speed * timePassed;

        if (topLayer.threejs.position[topLayer.direction] > 10) {
          gameLogic.missedTheSpot(endGame);
        }
      } else if (autopilotRef.current && !gameEndedRef.current) {
        gameLogic.splitBlockAndAddNextOneIfOverlaps(
          (newScore: number) => {
            setScore(newScore);
          },
          () => gameLogic.missedTheSpot(endGame),
        );
        setRobotPrecision();
      }

      const cameraHeight = gameLogic.constants.BOX_HEIGHT * (gameLogic.stackRef.current.length - 2) + 4;
      scene.updateCameraPosition(cameraHeight, speed, timePassed);

      physics.updatePhysics(timePassed, gameLogic.overhangsRef.current);

      if (scene.sceneRef.current && scene.cameraRef.current) {
        renderer.render(scene.sceneRef.current, scene.cameraRef.current);
      }
    },
    [
      calculateSpeed,
      autopilotRef,
      endGame,
      gameEndedRef,
      gameLogic,
      physics,
      renderer,
      robotPrecisionRef,
      scene,
      setRobotPrecision,
      setScore,
    ],
  );

  const animation = useAnimationLoop({ onFrame: onAnimationFrame });

  const initializeGame = useCallback(async () => {
    if (!containerRef.current) return;

    physics.initializePhysicsWorld();
    scene.initializeCamera();
    scene.initializeScene();
    renderer.initializeRenderer();
    resetGameState();
    gameLogic.setupInitialBlocks();

    setTimeout(() => {
      setGameState('instructions');
    }, 1500);
  }, [containerRef, physics, scene, renderer, resetGameState, gameLogic, setGameState]);

  const startGame = useCallback(() => {
    triggerHaptic();

    startGameState();
    gameLogic.resetGameLogic();

    physics.clearPhysicsWorld();
    scene.clearScene();
    scene.resetCamera();

    physics.initializePhysicsWorld();
    gameLogic.setupInitialBlocks();
  }, [startGameState, gameLogic, physics, scene, triggerHaptic]);

  const resetGame = useCallback(() => {
    triggerHaptic();

    animation.stopAnimation();

    renderer.clearRenderer();
    physics.clearPhysicsWorld();
    scene.clearAll();

    gameLogic.resetGameLogic();

    setGameState('loading');

    setTimeout(() => {
      initializeGame();
    }, 100);
  }, [animation, renderer, physics, scene, gameLogic, setGameState, initializeGame, triggerHaptic]);

  const handleGameAction = useCallback(() => {
    if (autopilotRef.current) {
      startGame();
    } else {
      gameLogic.splitBlockAndAddNextOneIfOverlaps(
        (newScore: number) => {
          setScore(newScore);
        },
        () => gameLogic.missedTheSpot(endGame),
      );
    }
  }, [autopilotRef, startGame, gameLogic, setScore, endGame]);

  useEffect(() => {
    if (gameState === 'instructions' && !animation.animationIdRef.current && renderer.rendererRef.current) {
      animation.startAnimation();
    }
  }, [gameState, animation, renderer.rendererRef]);

  useEffect(() => {
    scene.updateBackgroundColor(gameConfig.background.color);
  }, [gameConfig.background.color, scene.updateBackgroundColor]);

  useEffect(() => {
    initializeGame();
    animation.startAnimation();

    const handleResize = () => {
      scene.updateCameraAspect();
      renderer.updateRendererSize();

      if (scene.sceneRef.current && scene.cameraRef.current) {
        renderer.render(scene.sceneRef.current, scene.cameraRef.current);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      animation.stopAnimation();
      window.removeEventListener('resize', handleResize);
      renderer.clearRenderer();
    };
  }, []);

  return {
    score,
    gameState,
    startGame,
    resetGame,
    handleGameAction,
  };
}
